import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Download,
  FileText,
  BarChart3,
  Clock,
  TrendingUp,
  Loader2,
  Sparkles,
  RefreshCw
} from "lucide-react";

const Reports = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("grades");
  
  // Report data
  const [gradesReport, setGradesReport] = useState([]);
  const [attendanceReport, setAttendanceReport] = useState([]);
  const [analysisReport, setAnalysisReport] = useState(null);
  const [actas, setActas] = useState([]);
  const [selectedActa, setSelectedActa] = useState(null);

  // Loading states
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [generatingActa, setGeneratingActa] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  useEffect(() => {
    if (course) {
      loadReportData(activeTab);
    }
  }, [activeTab, course]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      toast.error("Error al cargar la capacitación");
      navigate("/courses");
    } finally {
      setLoading(false);
    }
  };

  const loadReportData = async (tab) => {
    switch (tab) {
      case "grades":
        if (gradesReport.length === 0) await fetchGradesReport();
        break;
      case "attendance":
        if (attendanceReport.length === 0) await fetchAttendanceReport();
        break;
      case "analysis":
        if (!analysisReport) await fetchAnalysisReport();
        await fetchActas();
        break;
    }
  };

  const fetchGradesReport = async () => {
    setLoadingGrades(true);
    try {
      const response = await axios.get(`${API}/reports/grades/${id}`);
      setGradesReport(response.data);
    } catch (error) {
      toast.error("Error al cargar reporte de calificaciones");
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchAttendanceReport = async () => {
    setLoadingAttendance(true);
    try {
      const response = await axios.get(`${API}/reports/attendance/${id}`);
      setAttendanceReport(response.data);
    } catch (error) {
      toast.error("Error al cargar reporte de asistencia");
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchAnalysisReport = async () => {
    setLoadingAnalysis(true);
    try {
      const response = await axios.get(`${API}/reports/analysis/${id}`);
      setAnalysisReport(response.data);
    } catch (error) {
      toast.error("Error al cargar análisis");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const fetchActas = async () => {
    try {
      const response = await axios.get(`${API}/reports/actas/${id}`);
      setActas(response.data);
    } catch (error) {
      console.error("Error fetching actas:", error);
    }
  };

  const generateActa = async () => {
    setGeneratingActa(true);
    try {
      // Genera el mismo PDF completo que el botón de Calificaciones
      const url = `${API}/reports/pdf/grades/${id}`;
      const filename = `acta_${id}.pdf`;

      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Acta descargada exitosamente");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al generar el acta";
      toast.error(message);
    } finally {
      setGeneratingActa(false);
    }
  };

  const downloadPdf = async (type, actaId = null) => {
    setDownloadingPdf(true);
    try {
      let url;
      let filename;
      
      switch (type) {
        case 'grades':
          url = `${API}/reports/pdf/grades/${id}`;
          filename = `calificaciones_${id}.pdf`;
          break;
        case 'attendance':
          url = `${API}/reports/pdf/attendance/${id}`;
          filename = `asistencia_${id}.pdf`;
          break;
        case 'acta':
          url = `${API}/reports/pdf/acta/${actaId}`;
          filename = `acta_${actaId}.pdf`;
          break;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al descargar PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 skeleton rounded" />
        <div className="h-96 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading">Reportes de la Capacitación</h1>
          <p className="text-slate-600">{course?.name}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="grades" className="gap-2">
            <FileText className="w-4 h-4" />
            Calificaciones
          </TabsTrigger>
          <TabsTrigger value="attendance" className="gap-2">
            <Clock className="w-4 h-4" />
            Asistencia
          </TabsTrigger>
          <TabsTrigger value="analysis" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Análisis IA
          </TabsTrigger>
        </TabsList>

        {/* Grades Report */}
        <TabsContent value="grades">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Calificaciones por Estudiante</CardTitle>
              <Button 
                variant="outline" 
                onClick={() => downloadPdf('grades')}
                disabled={downloadingPdf || gradesReport.length === 0}
                data-testid="download-grades-pdf"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar PDF
              </Button>
            </CardHeader>
            <CardContent>
              {loadingGrades ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : gradesReport.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No hay datos de calificaciones disponibles</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estudiante</TableHead>
                      <TableHead>Pre-test</TableHead>
                      <TableHead>Post-test</TableHead>
                      <TableHead>Mejora</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gradesReport.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{row.full_name}</p>
                            <p className="text-sm text-slate-500">{row.username}</p>
                          </div>
                        </TableCell>
                        <TableCell>{row.pretest_score !== null ? `${row.pretest_score.toFixed(1)}%` : '-'}</TableCell>
                        <TableCell>{row.posttest_score !== null ? `${row.posttest_score.toFixed(1)}%` : '-'}</TableCell>
                        <TableCell>
                          <span className={row.improvement > 0 ? 'text-green-600' : row.improvement < 0 ? 'text-red-600' : ''}>
                            {row.improvement > 0 ? '+' : ''}{row.improvement}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            row.status === 'completed' ? 'bg-green-100 text-green-800' :
                            row.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {row.status === 'completed' ? 'Completado' : row.status === 'failed' ? 'Reprobado' : 'En Progreso'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Report */}
        <TabsContent value="attendance">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Registro de Asistencia</CardTitle>
              <Button 
                variant="outline" 
                onClick={() => downloadPdf('attendance')}
                disabled={downloadingPdf || attendanceReport.length === 0}
                data-testid="download-attendance-pdf"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar PDF
              </Button>
            </CardHeader>
            <CardContent>
              {loadingAttendance ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : attendanceReport.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No hay datos de asistencia disponibles</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estudiante</TableHead>
                      <TableHead>Total Accesos</TableHead>
                      <TableHead>Último Acceso</TableHead>
                      <TableHead>Secciones Visitadas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendanceReport.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{row.full_name}</p>
                            <p className="text-sm text-slate-500">{row.username}</p>
                          </div>
                        </TableCell>
                        <TableCell>{row.access_count}</TableCell>
                        <TableCell>
                          {row.last_access 
                            ? new Date(row.last_access).toLocaleString()
                            : 'Sin accesos'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {row.sections_visited?.map((section, i) => (
                              <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs">
                                {section}
                              </span>
                            )) || '-'}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Analysis */}
        <TabsContent value="analysis">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Metrics */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Métricas de la Capacitación
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingAnalysis ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : analysisReport ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-primary">{analysisReport.total_enrolled}</p>
                        <p className="text-xs text-slate-500">Matriculados</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-600">{analysisReport.passed}</p>
                        <p className="text-xs text-slate-500">Aprobados</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-600">{analysisReport.failed}</p>
                        <p className="text-xs text-slate-500">Reprobados</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{analysisReport.in_progress}</p>
                        <p className="text-xs text-slate-500">En Progreso</p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-3">Promedios</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Pre-test</span>
                          <span className="font-bold text-lg">{analysisReport.avg_pretest}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Post-test</span>
                          <span className="font-bold text-lg">{analysisReport.avg_posttest}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Mejora</span>
                          <span className={`font-bold text-lg ${analysisReport.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {analysisReport.improvement > 0 ? '+' : ''}{analysisReport.improvement}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <Button 
                        onClick={generateActa} 
                        disabled={generatingActa}
                        className="w-full"
                        data-testid="generate-acta-btn"
                      >
                        {generatingActa ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generando...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generar Acta con IA
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No hay datos disponibles</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Acta Display */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Acta de Análisis
                </CardTitle>
                {selectedActa && (
                  <Button 
                    variant="outline" 
                    onClick={() => downloadPdf('acta', selectedActa.id)}
                    disabled={downloadingPdf}
                    data-testid="download-acta-pdf"
                  >
                    {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Descargar PDF
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {actas.length > 0 && !selectedActa && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-500 mb-2">Actas generadas anteriormente:</p>
                    <div className="space-y-2">
                      {actas.slice(0, 3).map((acta) => (
                        <button
                          key={acta.id}
                          onClick={() => setSelectedActa(acta)}
                          className="w-full text-left p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <p className="font-medium text-sm">Acta del {new Date(acta.generated_at).toLocaleDateString()}</p>
                          <p className="text-xs text-slate-500">{new Date(acta.generated_at).toLocaleTimeString()}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedActa ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                      <span className="text-sm text-slate-500">
                        Generada el {new Date(selectedActa.generated_at).toLocaleString()}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedActa(null)}>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Ver otras
                      </Button>
                    </div>
                    <div className="acta-preview max-h-[500px] overflow-y-auto p-4 bg-white border rounded-lg">
                      {selectedActa.content.split('\n').map((line, idx) => {
                        if (line.startsWith('# ')) {
                          return <h1 key={idx} className="text-2xl font-bold mt-4 mb-2">{line.slice(2)}</h1>;
                        } else if (line.startsWith('## ')) {
                          return <h2 key={idx} className="text-xl font-semibold mt-4 mb-2">{line.slice(3)}</h2>;
                        } else if (line.startsWith('### ')) {
                          return <h3 key={idx} className="text-lg font-medium mt-3 mb-1">{line.slice(4)}</h3>;
                        } else if (line.startsWith('- ') || line.startsWith('* ')) {
                          return <li key={idx} className="ml-4">{line.slice(2)}</li>;
                        } else if (line.startsWith('**') && line.endsWith('**')) {
                          return <p key={idx} className="font-bold">{line.slice(2, -2)}</p>;
                        } else if (line.trim()) {
                          return <p key={idx} className="mb-2">{line}</p>;
                        }
                        return <br key={idx} />;
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Genera un Acta con IA</h3>
                    <p className="max-w-md mx-auto">
                      Utiliza inteligencia artificial para generar un análisis completo del rendimiento 
                      de los estudiantes en esta capacitación, incluyendo métricas, conclusiones y recomendaciones.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
